/**
 * Generic tile compositing service.
 *
 * Any WMS overlay layer can opt in to compositing. When enabled for a layer,
 * this service fetches tiles for N recent dates and paints them newest-on-top
 * so sparse-coverage layers (SAR, Sentinel-2, Landsat) show complete imagery.
 *
 * Layers not using compositing get "best available" mode — GIBS serves the
 * most recent tile per pixel when the TIME parameter is omitted.
 */
import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { getCompositeDates, tileBbox3857, computeEndDate } from '../../lib/tile-math.js'
import { applyCloudMask, type PixelData } from '../../lib/image-processing.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { tileCacheService } from '../tile-cache/tile-cache-service.js'

const log = logger.create('composite')

const PROTOCOL = 'composite'
const TILE_SIZE = 256
const WMS_BASE = 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi'
const MIN_DATA_SIZE = 500 // transparent PNG is ~334 bytes
const REFRESH_DEBOUNCE_MS = 5000

export interface CompositeColorKey {
  r: number
  g: number
  b: number
  tolerance: number
}

export interface CompositeLayerConfig {
  /** Unique layer id (matches OverlayLayer.id) */
  id: string
  /** WMS LAYERS parameter value */
  wmsLayer: string
  /** WMS FORMAT (default image/png) */
  format?: string
  /** Number of days to look back for compositing */
  windowDays: number
  /** Processing latency in days — clamp end date this many days back */
  latencyDays?: number
  /** Max native zoom of the source data */
  maxNativeZoom?: number
  /** Min zoom to request tiles */
  minZoom?: number
  /** Map attribution string */
  attribution?: string
  /**
   * WMS LAYERS name for a cloud mask product (same satellite/date).
   * Pixels where the mask tile has alpha > 0 are treated as cloudy
   * and zeroed out before compositing.
   */
  cloudMaskLayer?: string
  /**
   * Colors to make transparent in each tile before compositing.
   * Useful for classification layers where "no data" / "no change" pixels
   * are rendered as opaque colors (e.g. OPERA DIST-ALERT "No Disturbance").
   */
  colorKeys?: CompositeColorKey[]
}

interface ActiveLayer {
  config: CompositeLayerConfig
  opacity: number
  currentEndDate: string
  sourceId: string
  layerId: string
}

class TileCompositeService {
  private map: maplibregl.Map | null = null
  private activeLayers = new Map<string, ActiveLayer>()
  private pendingRequests = new Map<string, AbortController>()
  private refreshTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private lastRefreshTime = new Map<string, number>()

  // Shared cache across all layers: "wmsLayer/z/x/y/date" → ImageData | null
  private tileCache = new Map<string, ImageData | null>()
  private readonly TILE_CACHE_MAX = 4096

  register(map: maplibregl.Map): void {
    this.map = map
    addProtocol(PROTOCOL, (params, abortController) => {
      return this.handleTileRequest(params, abortController)
    })
    timelineService.addListener(this.onTimelineChange)
  }

  unregister(): void {
    this.disableAll()
    timelineService.removeListener(this.onTimelineChange)
    try {
      removeProtocol(PROTOCOL)
    } catch (_) {
      /* may not be registered */
    }
    this.tileCache.clear()
    this.map = null
  }

  /** Check if a specific layer is compositing */
  isEnabled(layerId: string): boolean {
    return this.activeLayers.has(layerId)
  }

  /** Enable compositing for a layer */
  enable(config: CompositeLayerConfig, opacity = 0.7): void {
    if (!this.map || this.activeLayers.has(config.id)) {
      return
    }

    const sourceId = `composite-${config.id}`
    const layerId = `composite-layer-${config.id}`
    const endDate = this.computeEndDate(
      timelineService.getState().currentTime,
      config.latencyDays ?? 0
    )

    const active: ActiveLayer = {
      config,
      opacity,
      currentEndDate: endDate,
      sourceId,
      layerId,
    }

    this.activeLayers.set(config.id, active)
    this.addSourceAndLayer(active)
  }

  /** Disable compositing for a layer */
  disable(layerId: string): void {
    const active = this.activeLayers.get(layerId)
    if (!active || !this.map) {
      return
    }

    // Cancel pending requests for this layer
    for (const [key, controller] of this.pendingRequests) {
      if (key.startsWith(`${layerId}/`)) {
        controller.abort()
        this.pendingRequests.delete(key)
      }
    }

    const timer = this.refreshTimers.get(layerId)
    if (timer) {
      clearTimeout(timer)
      this.refreshTimers.delete(layerId)
    }
    this.lastRefreshTime.delete(layerId)

    if (this.map.getLayer(active.layerId)) {
      this.map.removeLayer(active.layerId)
    }
    if (this.map.getSource(active.sourceId)) {
      this.map.removeSource(active.sourceId)
    }

    this.activeLayers.delete(layerId)
  }

  /** Disable all composite layers */
  disableAll(): void {
    for (const id of [...this.activeLayers.keys()]) {
      this.disable(id)
    }
  }

  /** Set opacity for a composite layer */
  setOpacity(layerId: string, opacity: number): void {
    const active = this.activeLayers.get(layerId)
    if (!active || !this.map) {
      return
    }
    active.opacity = Math.max(0, Math.min(1, opacity))
    if (this.map.getLayer(active.layerId)) {
      this.map.setPaintProperty(active.layerId, 'raster-opacity', active.opacity)
    }
  }

  /** Get current opacity for a composite layer */
  getOpacity(layerId: string): number {
    return this.activeLayers.get(layerId)?.opacity ?? 0.7
  }

  private computeEndDate(time: Date, latencyDays: number): string {
    return computeEndDate(time, latencyDays)
  }

  private addSourceAndLayer(active: ActiveLayer): void {
    if (!this.map) {
      return
    }
    const { config, sourceId, layerId, opacity, currentEndDate } = active

    // Encode layer config in the tile URL so handleTileRequest can decode it
    let tileUrl = `${PROTOCOL}://${config.id}/{z}/{x}/{y}?end=${currentEndDate}&window=${config.windowDays}&wms=${encodeURIComponent(config.wmsLayer)}&fmt=${encodeURIComponent(config.format ?? 'image/png')}`
    if (config.cloudMaskLayer) {
      tileUrl += `&cmask=${encodeURIComponent(config.cloudMaskLayer)}`
    }

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: TILE_SIZE,
        minzoom: config.minZoom ?? 3,
        maxzoom: config.maxNativeZoom ?? 12,
        attribution: config.attribution ?? '',
      })
    }

    if (!this.map.getLayer(layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-opacity-transition': { duration: 0, delay: 0 },
            'raster-fade-duration': 0,
          },
        },
        config.id
      )
    }
  }

  private onTimelineChange = (state: Readonly<TimelineState>): void => {
    if (!this.map || this.activeLayers.size === 0) {
      return
    }

    for (const [id, active] of this.activeLayers) {
      const newEnd = this.computeEndDate(state.currentTime, active.config.latencyDays ?? 0)
      if (newEnd === active.currentEndDate) {
        continue
      }

      active.currentEndDate = newEnd

      // Debounce refresh per layer
      const now = performance.now()
      const lastRefresh = this.lastRefreshTime.get(id) ?? 0
      const elapsed = now - lastRefresh

      const existing = this.refreshTimers.get(id)
      if (existing) {
        clearTimeout(existing)
      }

      if (elapsed >= REFRESH_DEBOUNCE_MS) {
        this.refreshLayer(active)
        this.lastRefreshTime.set(id, now)
      } else {
        const delay = REFRESH_DEBOUNCE_MS - elapsed
        this.refreshTimers.set(
          id,
          setTimeout(() => {
            this.refreshTimers.delete(id)
            this.lastRefreshTime.set(id, performance.now())
            this.refreshLayer(active)
          }, delay)
        )
      }
    }
  }

  private refreshLayer(active: ActiveLayer): void {
    if (!this.map) {
      return
    }
    const { sourceId, layerId } = active

    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId)
    }
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId)
    }

    this.addSourceAndLayer(active)
  }

  // ===== Tile fetching and compositing =====

  private getCompositeDates(endDate: string, windowDays: number): string[] {
    return getCompositeDates(endDate, windowDays)
  }

  private async fetchSingleTile(
    wmsLayer: string,
    format: string,
    z: number,
    x: number,
    y: number,
    date: string,
    signal: AbortSignal,
    colorKeys?: CompositeColorKey[]
  ): Promise<ImageData | null> {
    const cacheKey = `${wmsLayer}/${z}/${x}/${y}/${date}`
    const cached = this.tileCache.get(cacheKey)
    if (cached !== undefined) {
      return cached
    }

    const bbox = TileCompositeService.tileBbox3857(z, x, y)

    const params = new URLSearchParams()
    params.set('SERVICE', 'WMS')
    params.set('VERSION', '1.1.1')
    params.set('REQUEST', 'GetMap')
    params.set('LAYERS', wmsLayer)
    params.set('STYLES', '')
    params.set('FORMAT', format)
    params.set('TRANSPARENT', 'TRUE')
    params.set('SRS', 'EPSG:3857')
    params.set('WIDTH', String(TILE_SIZE))
    params.set('HEIGHT', String(TILE_SIZE))
    params.set('BBOX', bbox)
    params.set('TIME', date)

    const url = `${WMS_BASE}?${params.toString()}`
    // Route upstream WMS tile fetch through the persistent tile cache so
    // blobs survive reloads. The decoded ImageData stays in our RAM cache
    // (decoding is expensive and ImageData isn't serializable).
    const cacheSourceId = `composite-src:${wmsLayer}:${date}`
    const blob = await tileCacheService.getTile(cacheSourceId, url, z, x, y, { signal })

    if (!blob) {
      log.debug(`[composite] ${cacheKey}: fetch failed or unavailable`)
      this.cacheTile(cacheKey, null)
      return null
    }

    if (blob.size < MIN_DATA_SIZE) {
      // Transparent/empty tile
      this.cacheTile(cacheKey, null)
      return null
    }

    log.debug(`[composite] ${cacheKey}: ${blob.size} bytes (has data)`)

    const bitmap = await createImageBitmap(blob, { premultiplyAlpha: 'none' })
    const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(bitmap, 0, 0, TILE_SIZE, TILE_SIZE)
    bitmap.close()

    const imageData = ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE)
    if (colorKeys?.length) {
      const px = imageData.data
      for (let i = 0; i < px.length; i += 4) {
        for (const key of colorKeys) {
          if (
            Math.abs(px[i] - key.r) <= key.tolerance &&
            Math.abs(px[i + 1] - key.g) <= key.tolerance &&
            Math.abs(px[i + 2] - key.b) <= key.tolerance
          ) {
            px[i + 3] = 0
            break
          }
        }
      }
    }
    this.cacheTile(cacheKey, imageData)
    return imageData
  }

  private cacheTile(key: string, data: ImageData | null): void {
    if (this.tileCache.size >= this.TILE_CACHE_MAX) {
      const firstKey = this.tileCache.keys().next().value!
      this.tileCache.delete(firstKey)
    }
    this.tileCache.set(key, data)
  }

  private static tileBbox3857(z: number, x: number, y: number): string {
    return tileBbox3857(z, x, y)
  }

  /**
   * Apply a cloud mask to imagery, delegating to the pure function in
   * lib/image-processing.ts. Adds debug logging around the call.
   */
  private static applyCloudMaskWithLog(imagery: ImageData, mask: ImageData): ImageData {
    const masked: PixelData = applyCloudMask(imagery, mask)

    // Debug logging — count how many pixels were masked
    const totalPixels = masked.data.length / 4
    let maskedCount = 0
    for (let i = 0; i < masked.data.length; i += 4) {
      if (masked.data[i + 3] === 0 && imagery.data[i + 3] !== 0) {
        maskedCount++
      }
    }

    const pct = ((maskedCount / totalPixels) * 100).toFixed(1)
    log.debug(`[cloud-mask] ${maskedCount}/${totalPixels} pixels masked (${pct}%)`)

    if (maskedCount === 0) {
      const maskData = mask.data
      for (let i = 0; i < Math.min(maskData.length, 400); i += 4) {
        const r = maskData[i],
          g = maskData[i + 1],
          b = maskData[i + 2],
          a = maskData[i + 3]
        if (r > 0 || g > 0 || b > 0 || a > 0) {
          log.debug(`[cloud-mask] First non-zero mask pixel at ${i / 4}: rgba(${r},${g},${b},${a})`)
          break
        }
      }
      log.debug(`[cloud-mask] Mask appears fully transparent — cloud mask may not be applying`)
    }

    // Construct a real ImageData for use with canvas putImageData.
    // The Uint8ClampedArray from applyCloudMask is backed by a plain ArrayBuffer,
    // but TypeScript's strict types need the cast to satisfy ImageData's constructor.
    return new ImageData(
      new Uint8ClampedArray(masked.data.buffer) as unknown as Uint8ClampedArray<ArrayBuffer>,
      masked.width,
      masked.height
    )
  }

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ArrayBuffer }> {
    // URL format: composite://{layerId}/{z}/{x}/{y}?end={date}&window={days}&wms={layer}&fmt={format}
    const match = params.url.match(/composite:\/\/([^/]+)\/(\d+)\/(\d+)\/(\d+)\?(.+)/)
    if (!match) {
      throw new Error(`Invalid composite tile URL: ${params.url}`)
    }

    const layerId = match[1]
    const z = parseInt(match[2])
    const x = parseInt(match[3])
    const y = parseInt(match[4])
    const urlParams = new URLSearchParams(match[5])
    const endDate = urlParams.get('end') ?? ''
    const windowDays = parseInt(urlParams.get('window') ?? '12')
    const wmsLayer = decodeURIComponent(urlParams.get('wms') ?? '')
    const format = decodeURIComponent(urlParams.get('fmt') ?? 'image/png')
    const cloudMaskLayer = urlParams.get('cmask')
      ? decodeURIComponent(urlParams.get('cmask')!)
      : null

    const activeLayer = this.activeLayers.get(layerId)
    const colorKeys = activeLayer?.config.colorKeys

    const requestKey = `${layerId}/${z}/${x}/${y}/${endDate}`
    this.pendingRequests.set(requestKey, abortController)

    try {
      const dates = this.getCompositeDates(endDate, windowDays)
      const signal = abortController.signal

      // Fetch all dates in parallel — imagery and cloud masks together
      const results = await Promise.all(
        dates.map(async date => {
          const tile = await this.fetchSingleTile(
            wmsLayer,
            format,
            z,
            x,
            y,
            date,
            signal,
            colorKeys
          ).catch(() => null)
          if (!tile) {
            return { tile: null, masked: false }
          }
          if (!cloudMaskLayer) {
            return { tile, masked: false }
          }

          // Fetch cloud mask for this date
          const mask = await this.fetchSingleTile(
            cloudMaskLayer,
            'image/png',
            z,
            x,
            y,
            date,
            signal
          ).catch(() => null)
          if (!mask) {
            return { tile, masked: false }
          } // no mask data — keep tile unmasked

          // Apply mask: where cloud mask has alpha > 0, zero out imagery pixel
          return { tile: TileCompositeService.applyCloudMaskWithLog(tile, mask), masked: true }
        })
      )

      const nonNull = results.filter(r => r.tile !== null).length
      const maskedCount = results.filter(r => r.masked).length
      if (z >= 5) {
        log.debug(
          `[composite] ${layerId} ${z}/${x}/${y}: ${nonNull}/${results.length} tiles with data, ${maskedCount} cloud-masked (${dates[0]} to ${dates[dates.length - 1]})`
        )
      }

      // Composite: unmasked tiles first (background), then masked tiles on top.
      // Within each group, oldest first so newest paints over oldest.
      // This ensures cloud-filtered imagery always wins over unfiltered.
      const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
      const ctx = canvas.getContext('2d')!
      const tmpCanvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE)
      const tmpCtx = tmpCanvas.getContext('2d')!

      // Pass 1: unmasked tiles (oldest first)
      for (let i = results.length - 1; i >= 0; i--) {
        const { tile, masked } = results[i]
        if (!tile || masked) {
          continue
        }
        tmpCtx.putImageData(tile, 0, 0)
        ctx.drawImage(tmpCanvas, 0, 0)
      }
      // Pass 2: masked tiles (oldest first, painted on top)
      for (let i = results.length - 1; i >= 0; i--) {
        const { tile, masked } = results[i]
        if (!tile || !masked) {
          continue
        }
        tmpCtx.putImageData(tile, 0, 0)
        ctx.drawImage(tmpCanvas, 0, 0)
      }

      const blob = await canvas.convertToBlob({ type: 'image/png' })
      const arrayBuffer = await blob.arrayBuffer()
      return { data: arrayBuffer }
    } finally {
      this.pendingRequests.delete(requestKey)
    }
  }
}

export const tileCompositeService = new TileCompositeService()
